import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { LocationApiAdapter } from '../adapters/LocationApiAdapter';
import { LocationFilters } from '../models/Location';
import { LocationService } from '../services/LocationService';

// 🎯 Hook con scroll infinito
export const useLocations = (filters: LocationFilters = {}) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new LocationApiAdapter(httpClient);
    const locationService = new LocationService(apiAdapter);

    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending
    } = useInfiniteQuery({
        queryKey: ['locations', filters],
        queryFn: ({ pageParam = 1 }) =>
            locationService.getLocationsPaginated({
                ...filters,
                page: pageParam
            }),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 1,
        staleTime: 5 * 60 * 1000,
        placeholderData: (previousData) => previousData, // 🎯 Mantiene datos anteriores durante nuevas búsquedas
    });

    // 🎯 Aplanar todas las páginas en un array único
    const locations = data?.pages.flatMap(page => page.locations) ?? [];

    // 🎯 Obtener el total del API (siempre será el mismo en todas las páginas)
    const totalFromApi = data?.pages?.[0]?.total ?? 0;

    return {
        locations,
        isLoading: isLoading || isPending,
        isFetching, // 🎯 Para controlar opacity mientras se hace fetch
        error,
        refetch,
        // 🎯 Nuevas props para scroll infinito
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        isFetchingNextPage,
        // 🎯 Stats útiles
        totalPages: data?.pages.length ?? 0,
        totalLocations: locations.length,
        total: totalFromApi, // 🎯 Total real del API (con filtros)
    }
}

// 🎯 Hook original para compatibilidad hacia atrás (sin scroll infinito)
export const useLocationsSimple = (filters: LocationFilters = {}) => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new LocationApiAdapter(httpClient);
    const locationService = new LocationService(apiAdapter);

    const {
        data: locations = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['locations-simple', filters],
        queryFn: () => locationService.getAllLocations(filters),
        staleTime: 5 * 60 * 1000,
        placeholderData: (previousData) => previousData, // 🎯 Mantiene datos anteriores durante nuevas búsquedas
    });

    return {
        locations,
        isLoading,
        error,
        refetch
    }
}